import {
  Check,
  CircleCheckBig,
  CircleX,
  Lock,
  OctagonAlert,
  PackageMinus,
  PackagePlus,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  UserRound,
  Flag,
  type LucideIcon,
} from 'lucide-react';
import type { ActivityLogDto } from '../types/activity.types';

/**
 * Kind → icon + tint, and the filter-pill grouping (E9 G1/G6, design §1.3–1.4).
 *
 * ## This is the ONLY place a kind means anything on the client
 *
 * ⚠️ There is deliberately **no `ActionType` → kind mapping anywhere in `src/`**. That would be a second
 * registry needing lockstep with the server's projection map across ~77 event types, and it would silently
 * mis-tint every event type added server-side. The server assigns `Kind` per EventType and stores it on the
 * row; this file only decides what a kind LOOKS like.
 *
 * ## Colour means outcome
 *
 * Tinted tiles use the soft-tint idiom `bg-token/10 text-token`. The four muted kinds carry **no colour at
 * all** — matching the design system's chart rule that colour is reserved for outcome. A `system` refresh and
 * an `error` must not compete for the same attention.
 */

/**
 * The client half of the kind vocabulary — the mirror of `ActivityKinds.All` in
 * `CSC_CORE/Features/Activity/ActivityKinds.cs` (E5 contract C23).
 *
 * ⚠️ The shape is load-bearing: one single-quoted token per line. `npm run scan:tokens` extracts
 * this array with a per-line regex and fails hard on any server token missing here
 * (`undrawableTokens` must stay 0) — because `resolveKind` falls back to the muted `system` tile
 * by design, a missing token is invisible to every runtime check while its rows render untinted
 * under the wrong filter pill.
 *
 * A STRING array against a STRING column, deliberately. Server enums reach this client as
 * NUMBERS, so a union declared against a C# enum would be silently always-mismatched and `tsc`
 * could not catch it. `Kind` is stored as a lowercase token precisely so the wire shape and the
 * union derived below are the same thing.
 */
export const KIND_TOKENS = [
  'create',
  'edit',
  'status',
  'approve',
  'send',
  'system',
  'assign',
  'attach',
  'error',
] as const;

/**
 * Derived, never hand-written (86d43b6gk — the hand-written copy in `../types` drifted silently).
 * Adding a token to `KIND_TOKENS` extends this union, and the `Record<ActivityKind, …>`
 * annotation on `KIND_MAP` then makes `tsc` demand the matching glyph. Re-exported from
 * `../types` so wire-format importers keep their path.
 */
export type ActivityKind = (typeof KIND_TOKENS)[number];

export interface KindDescriptor {
  icon: LucideIcon;
  /** Tailwind classes for the 24×24 tile. Semantic tokens only — must hold in light, dark and Industrial. */
  tile: string;
}

const MUTED_TILE = 'bg-muted text-muted-foreground';

export const KIND_MAP: Record<ActivityKind, KindDescriptor> = {
  create: { icon: Plus, tile: 'bg-info/10 text-info' },
  edit: { icon: Pencil, tile: 'bg-warning/10 text-warning' },
  status: { icon: Flag, tile: 'bg-info/10 text-info' },
  approve: { icon: Check, tile: 'bg-success/10 text-success' },
  send: { icon: Send, tile: 'bg-info/10 text-info' },
  system: { icon: RefreshCw, tile: MUTED_TILE },
  assign: { icon: UserRound, tile: MUTED_TILE },
  attach: { icon: Paperclip, tile: MUTED_TILE },
  error: { icon: OctagonAlert, tile: 'bg-destructive/10 text-destructive' },
};

/**
 * The kind a row renders as.
 *
 * `null` — every row written before E5, permanently — resolves to `system`: muted, no colour, no guess. An
 * unknown token resolves the same way rather than throwing, because a render path is the worst possible place
 * to discover a server-side typo, and the database CHECK constraint already makes one impossible to insert.
 *
 * ⚠️ Membership via the TOKEN ARRAY, never `in KIND_MAP`. The `in` operator walks the prototype chain,
 * so a prototype key as a token (`"constructor"`, `"toString"`) passed the check and `KIND_MAP[kind]`
 * returned a function, not a descriptor — crashing the row render. The same rule the icon/tone lookups
 * below already follow.
 */
export function resolveKind(kind: string | null | undefined): ActivityKind {
  return kind && (KIND_TOKENS as readonly string[]).includes(kind) ? (kind as ActivityKind) : 'system';
}

export function kindDescriptor(kind: string | null | undefined): KindDescriptor {
  return KIND_MAP[resolveKind(kind)];
}

/**
 * The client half of the ICON vocabulary — the mirror of `ActivityIcons.All` in
 * `CSC_CORE/Features/Activity/ActivityIcons.cs` (86d43a729). The server declares one of these per
 * projection entry where the event earns a more specific glyph than its kind's default, so the tile says
 * WHAT happened — `package-plus` for a put-away, `lock` for a close — not just which filter bucket the row
 * lands in.
 *
 * ⚠️ Same load-bearing shape as `KIND_TOKENS`: one single-quoted token per line — `npm run scan:tokens`
 * extracts this array per-line against the server vocabulary and fails hard on any server token missing
 * here, because `rowDescriptor`'s fallback makes a missing token invisible to every runtime check.
 */
export const ICON_TOKENS = [
  'package-plus',
  'package-minus',
  'circle-check-big',
  'lock',
  'circle-x',
] as const;

export type IconToken = (typeof ICON_TOKENS)[number];

/**
 * The client half of the TILE-TONE vocabulary — the mirror of `ActivityTones.All` in
 * `CSC_CORE/Features/Activity/ActivityTones.cs` (86d43a729). Declared only where the OUTCOME earns a colour
 * the kind's default doesn't give it (the two completion milestones turn `success`); a tone never moves a
 * row between filter pills — it tints the tile and nothing else.
 *
 * ⚠️ Same load-bearing shape as `KIND_TOKENS`: one single-quoted token per line, for `npm run scan:tokens`.
 */
export const TILE_TONE_TOKENS = [
  'success',
  'warning',
  'info',
  'destructive',
] as const;

export type TileToneToken = (typeof TILE_TONE_TOKENS)[number];

/** Token → glyph. The `Record<IconToken, …>` annotation makes `tsc` demand a glyph per token. */
export const ICON_GLYPHS: Record<IconToken, LucideIcon> = {
  'package-plus': PackagePlus,
  'package-minus': PackageMinus,
  'circle-check-big': CircleCheckBig,
  lock: Lock,
  'circle-x': CircleX,
};

/** Token → tile classes, in the same soft-tint idiom as `KIND_MAP`. Semantic tokens only. */
export const TILE_TONES: Record<TileToneToken, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
  destructive: 'bg-destructive/10 text-destructive',
};

// Membership guards — the evidencePrimitives.ts rule: NEVER index a Record with a raw wire string, because
// `ICON_GLYPHS['constructor']` hands back a truthy prototype member. `Array.prototype.includes` consults no
// prototype chain, so the wire string is proven a member before it ever touches the Record.
function isIconToken(token: string): token is IconToken {
  return (ICON_TOKENS as readonly string[]).includes(token);
}

function isTileToneToken(token: string): token is TileToneToken {
  return (TILE_TONE_TOKENS as readonly string[]).includes(token);
}

/**
 * The descriptor a ROW renders: the server-declared icon/tone tokens layered over the kind defaults.
 *
 * This is a display VOCABULARY keyed on server-declared tokens with a fallback — NOT an ActionType
 * registry (the header rule stands: the server decides which EVENT carries which token; this file only
 * decides what a TOKEN looks like). The fallback is per property: a declared icon rides the kind tile, a
 * declared tone rides the kind glyph, and an absent or unknown token falls back to that property's kind
 * default — a render path is the worst place to discover a server-side typo, and `scan:tokens` +
 * `ActivityProjectionMap.Validate` already refuse the builds that could produce one.
 */
export function rowDescriptor(row: Pick<ActivityLogDto, 'kind' | 'icon' | 'tileTone'>): KindDescriptor {
  const base = kindDescriptor(row.kind);
  return {
    icon: row.icon && isIconToken(row.icon) ? ICON_GLYPHS[row.icon] : base.icon,
    tile: row.tileTone && isTileToneToken(row.tileTone) ? TILE_TONES[row.tileTone] : base.tile,
  };
}

/**
 * The four filter pills, verbatim from design §1.3. The grouping lives HERE, on the client, because it is a
 * presentation decision — the server takes whatever kind set it is handed and never needs to know what a pill
 * is.
 */
export const PILL_GROUPS = [
  { id: 'all', label: 'All', kinds: null },
  { id: 'edits', label: 'Edits', kinds: ['edit'] },
  { id: 'status', label: 'Status', kinds: ['status', 'approve', 'create'] },
  { id: 'system', label: 'System', kinds: ['system', 'error', 'send', 'assign', 'attach'] },
] as const satisfies readonly {
  id: string;
  label: string;
  kinds: readonly ActivityKind[] | null;
}[];

export type PillId = (typeof PILL_GROUPS)[number]['id'];

export interface PillView {
  id: PillId;
  label: string;
  count: number;
  kinds: readonly ActivityKind[] | null;
}

/**
 * Which pills to render, and their counts, from the server's whole-record `kindCounts`.
 *
 * Two rules, both from the design:
 * - **Only pills whose kinds actually exist for this record** — "an empty filter is worse than no filter".
 *   A group summing to zero is omitted entirely rather than shown greyed at `0`.
 * - **`All` is always present**, even at zero, because it is the state the panel opens in; hiding it would
 *   leave an empty record with no pill row at all and nothing to return to after filtering.
 *
 * Counts are whole-record, not per-page: a picklist produced 49 rows in one day against a page size of 25, so
 * counting the loaded page would show a number that changed as the user scrolled.
 */
export function buildPills(kindCounts: Record<string, number> | null | undefined): PillView[] {
  const counts = kindCounts ?? {};
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return PILL_GROUPS.flatMap<PillView>((group) => {
    if (group.kinds === null) {
      return [{ id: group.id, label: group.label, count: total, kinds: null }];
    }

    const count = group.kinds.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0);
    if (count === 0) return [];

    return [{ id: group.id, label: group.label, count, kinds: group.kinds }];
  });
}
